import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountsUsersCountByState} from 'src/openapi/admin/apis/AccountsUsersCountsApi';
import {UserState} from 'src/openapi/admin/models/UserState';
import {useAppDispatch} from 'src/store/hooks';

import {
    userConferencesLicensedUsersActions,
    userConferencesLicensedUsersDataSelector,
    userConferencesLicensedUsersResultSelector,
} from '../slices/user-conferences-licensed-users.slice';
import {userConferencesStateSelector} from '../slices/user-conferences-state.slice';

import type {UserConferencesCacheKeys} from '../typedef';

type Props = {
    accountIds: number[],
    totalCount: number,
};

export const useGetUserConferencesLicensedUsers = ({accountIds, totalCount}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(() => apiDispatch(
        getAccountsUsersCountByState({
            getAccountsUsersCountsByStateRequest: {
                accountIds: [...new Set(accountIds)],
                userState: UserState.LICENSED,
            },
        }),
    ), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, number>,
        dataCacheKeys: UserConferencesCacheKeys,
    ) => {
        dispatch(userConferencesLicensedUsersActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    return useCachedTable({
        dataSelector: userConferencesLicensedUsersDataSelector,
        stateSelector: userConferencesStateSelector,
        resultSelector: userConferencesLicensedUsersResultSelector,
        actions: userConferencesLicensedUsersActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
    });
};
